import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { LockKey, FloppyDisk, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PermissionMatrixPanel({ isAdmin }) {
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/finance/permissions/matrix");
      setData(d);
      setMatrix(d.matrix);
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (role, action) => {
    if (!isAdmin || role === "super_admin") return;
    setMatrix((m) => ({ ...m, [role]: { ...m[role], [action]: !m[role]?.[action] } }));
    setDirty(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/finance/permissions/matrix", { matrix });
      setData(d); setMatrix(d.matrix); setDirty(false);
      toast.success("Permission matrix saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    try {
      const { data: d } = await api.post("/finance/permissions/reset");
      setData(d); setMatrix(d.matrix); setDirty(false);
      toast.success("Matrix reset to defaults");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return null;

  return (
    <div className="dc-card overflow-hidden" data-testid="permission-matrix-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
        <div>
          <div className="flex items-center gap-2">
            <LockKey size={16} weight="fill" style={{ color: "#1E5CBA" }} />
            <h3 className="font-display text-[15px] font-semibold text-slate-900">Finance permission matrix</h3>
          </div>
          <div className="text-[11px] text-slate-400">
            {data.roles.length} roles × {data.actions.length} actions · enforced on every guarded finance endpoint
            {data.meta?.updated_by && ` · last edited by ${data.meta.updated_by}`}
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-8" onClick={reset} data-testid="perm-reset-btn">
              <ArrowCounterClockwise size={12} className="mr-1" /> Defaults
            </Button>
            <Button size="sm" className="btn-primary-blue h-8" onClick={save} disabled={!dirty || saving} data-testid="perm-save-btn">
              <FloppyDisk size={12} className="mr-1" /> {saving ? "Saving…" : "Save matrix"}
            </Button>
          </div>
        )}
      </div>
      <div className="responsive-table-wrap overflow-x-auto">
        <table className="w-full min-w-[1100px] text-xs">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/60">
              <th className="sticky left-0 bg-slate-50/95 px-4 py-2 text-left font-semibold text-slate-600">Role</th>
              {data.actions.map((a) => (
                <th key={a.key} className="px-1 py-2 text-center font-medium text-slate-500" title={a.label}>
                  <div className="mx-auto w-16 leading-tight text-[10px]">{a.label}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.roles.map((r) => (
              <tr key={r.key} className="border-b border-slate-50 hover:bg-slate-50/40" data-testid={`perm-row-${r.key}`}>
                <td className="sticky left-0 bg-white px-4 py-1.5">
                  <span className="font-semibold text-slate-800">{r.label}</span>
                  {r.locked && <span className="ml-1 text-[9px] font-bold text-slate-400">LOCKED</span>}
                  <div className="text-[9px] text-slate-400">{r.group}</div>
                </td>
                {data.actions.map((a) => (
                  <td key={a.key} className="px-1 py-1.5 text-center">
                    <Checkbox
                      checked={!!matrix[r.key]?.[a.key]}
                      disabled={!isAdmin || r.locked}
                      onCheckedChange={() => toggle(r.key, a.key)}
                      data-testid={`perm-${r.key}-${a.key}`}
                      className="mx-auto"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
